<template>
  <div class="query-container">
    <div :class="['query', { expanded: !loading }, { error: error.message }]">
      {{ query.queryText }}
    </div>
    <div class="error-container" v-if="error.message">
      <div class="error-title">Error</div>
      <div class="error-message">{{ error.message }}</div>
    </div>
    <div class="response-container" v-if="markdown && markdown.length > 0">
      <AskMarkdownRenderer
        :hovered="hoveredLink"
        :markdown="markdown"
        :contexts="contexts"
        @md-event="handleMarkdownEvent"
      ></AskMarkdownRenderer>
      <div class="response-links" v-if="links">
        <div class="links-container">
          <div
            v-for="(link, i) in sortedLinks.quoted"
            :key="i"
            :class="['link', { hovered: hoveredLink === link.id }]"
            @mouseover="hoveredLink = link.id"
            @mouseleave="hoveredLink = null"
            @click="goToDocument(link.id)"
          >
            <el-tooltip effect="light" :disabled="!contexts[link.id]">
              <div slot="content" class="label-tooltip">
                {{ contexts[link.id] }}
              </div>
              <span
                >{{
                  citationLabels[link.id] ? citationLabels[link.id] + '. ' : ''
                }}{{ link.label }}</span
              >
            </el-tooltip>
          </div>
        </div>
      </div>
      <details
        class="infos-container"
        v-if="infos.lastChunkTime && devMode"
        content="Additional data"
      >
        <summary>Additional data</summary>
        <div>Time to first chunk : {{ timeRequestToFirstChunk }}s</div>
        <div>Time first to last chunk : {{ timeFirstChunkToLastChunk }}s</div>
        <div>Total time: {{ timeRequestToLastChunk }}s</div>
      </details>
      <details
        class="infos-container"
        v-if="sortedLinks.nonQuoted && sortedLinks.nonQuoted.length"
        content="Additional sources"
      >
        <summary>Additional sources</summary>
        <div class="response-links mt-2">
          <div class="links-container">
            <div
              v-for="(link, i) in sortedLinks.nonQuoted"
              :key="i"
              class="link"
              @click="goToDocument(link.id)"
            >
              <el-tooltip effect="light" :disabled="contexts[link.id] === '[]'">
                <div slot="content" class="label-tooltip">
                  {{ contexts[link.id] }}
                </div>
                <span>{{ link.label }}</span>
              </el-tooltip>
            </div>
          </div>
        </div>
      </details>
      <div :class="['feedbacks-container', { disabled: feedback }]">
        <div
          :class="['upvote', { selected: feedback === 'up' }]"
          @click="handleFeedback(true)"
        >
          <font-awesome-icon
            :icon="[feedback === 'up' ? 'fas' : 'fal', 'thumbs-up']"
          />
        </div>
        <el-popover
          placement="bottom-end"
          width="200"
          v-model="displayFeedbackPopover"
        >
          <div class="feedback-form">
            <el-select v-model="feedbackForm.type" placeholder="Feedback type">
              <el-option
                v-for="item in feedbackForm.options"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
            <el-input
              v-model="feedbackForm.message"
              placeholder="Feedback message"
              type="textarea"
              :autosize="{ minRows: 2, maxRows: 4 }"
            />
            <el-button
              type="primary"
              @click="handleFeedback(false)"
              :disabled="!feedbackForm.message || !feedbackForm.type"
              >Send</el-button
            >
          </div>
          <div
            slot="reference"
            :class="['downvote', { selected: feedback === 'down' }]"
          >
            <font-awesome-icon
              :icon="[feedback === 'down' ? 'fas' : 'fal', 'thumbs-down']"
            />
          </div>
        </el-popover>
      </div>
    </div>
    <div v-if="loading">
      <div class="loading-container">
        <div class="loading-dot"></div>
        <div class="loading-dot"></div>
        <div class="loading-dot"></div>
      </div>
    </div>
  </div>
</template>

<script>
import AskMarkdownRenderer from './AskMarkdownRenderer.vue';

export default {
  name: 'AskQuery',
  components: { AskMarkdownRenderer },
  props: {
    query: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    response: {
      type: String,
      default: null,
    },
    id: {
      type: String,
      default: '',
    },
    markdown: {
      type: String,
      default: () => '',
    },
    error: {
      type: Object,
      default: () => ({}),
    },
    links: {
      type: Array,
      default: () => [],
    },
    citationLabels: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    feedback: {
      type: String,
      default: null,
    },
    infos: {
      type: Object,
      default: () => ({}),
    },
    devMode: {
      type: Boolean,
      default: false,
    },
    contexts: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    hoveredLink: null,
    displayFeedbackPopover: false,
    feedbackForm: {
      message: null,
      type: null,
      options: [
        {
          label: 'Product',
          value: 'product',
        },
        {
          label: 'AI',
          value: 'ai',
        },
      ],
    },
    sortedLinks: { quoted: [], nonQuoted: [] },
  }),
  computed: {
    timeRequestToFirstChunk() {
      return (this.infos.firstChunkTime - this.infos.requestTime) / 1000;
    },
    timeRequestToLastChunk() {
      return (this.infos.lastChunkTime - this.infos.requestTime) / 1000;
    },
    timeFirstChunkToLastChunk() {
      return (this.infos.lastChunkTime - this.infos.firstChunkTime) / 1000;
    },
    infoTooltipContent() {
      return `Request to first chunk: ${this.timeRequestToFirstChunk}s |
      Request to last chunk: ${this.timeRequestToLastChunk}s |
      First chunk to last chunk: ${this.timeFirstChunkToLastChunk}s`;
    },
  },
  methods: {
    handleFeedback(value) {
      this.displayFeedbackPopover = false;
      this.$emit(
        'feedback',
        {
          feedbackType: value ? null : this.feedbackForm.type,
          rating: value ? 'up' : 'down',
          content: value ? null : { text: this.feedbackForm.message },
        },
        this.query,
        this.id,
      );
      this.feedbackForm.message = null;
      this.feedbackForm.type = null;
    },
    goToDocument(id) {
      const document = this.links.find((d) => d.id === id);
      this.$emit('go-to-document', document);
    },
    handleMarkdownEvent(event) {
      if (event.type == 'leave') {
        this.hoveredLink = null;
      } else if (event.type == 'hover') {
        this.hoveredLink = event.id;
      } else if (event.type == 'go-to-document') {
        this.goToDocument(event.id);
      }
    },
    computeSortedLinks() {
      this.sortedLinks = this.links.reduce(
        (acc, val) => {
          if (this.citationLabels[val.id]) acc.quoted.push(val);
          else acc.nonQuoted.push(val);
          return acc;
        },
        { quoted: [], nonQuoted: [] },
      );
    },
  },
  watch: {
    citationLabels: {
      handler() {
        this.computeSortedLinks();
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>

<style scoped lang="scss">
.query-container {
  display: flex;
  flex-direction: column;
  width: fit-content;
  max-width: 100%;
}

.query {
  font-size: 14px;
  font-weight: 600;
  background-color: $purple-5-mayday;
  color: white;
  padding: 8px 16px;
  border-radius: 8px;
  margin-bottom: 8px;
  transition: all 0.2s ease;
  width: fit-content;

  &.expanded {
    border-radius: 8px 8px 0 0;
    margin-bottom: -8px;
    padding-bottom: 16px;
  }

  &.error {
    background-color: $red-5-mayday;
  }
}

.error-container {
  background-color: $red-1-mayday;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  position: relative;
}

.response-container {
  background-color: $grey-2-mayday;
  border-radius: 8px;
  padding: 8px 16px 16px 16px;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  position: relative;
}

.response-text {
  .link {
    color: $purple-5-mayday;
    font-size: 8px;
    border: 1px solid $purple-5-mayday;
    border-radius: 4px;
    padding: 2px 4px;
    background-color: white;
    cursor: pointer;
    transition: all 0.2s ease;

    &.hovered {
      background-color: $purple-5-mayday;
      color: white;
    }
  }
}

.response-links {
  gap: 4px;
  display: flex;
  align-items: center;
  width: 100%;

  .links-container {
    display: flex;
    align-items: center;
    gap: 4px;
    width: 100%;
    overflow: auto;
  }

  .link {
    display: flex;
    align-items: center;
    gap: 4px;
    border: 1px solid $purple-5-mayday;
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 12px;
    color: $purple-5-mayday;
    cursor: pointer;
    transition: all 0.2s ease;
    max-width: fit-content;
    overflow: hidden;
    flex: 1;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &.hovered {
      background-color: $purple-5-mayday;
      color: white;
    }
  }
}

.infos-container {
  color: $grey-6-mayday;
  font-size: 10px;
}

.feedbacks-container {
  display: flex;
  align-items: center;
  position: absolute;
  background-color: white;
  overflow: hidden;
  border: 1px solid $grey-2-mayday;
  border-radius: 8px;
  bottom: -8px;
  right: 8px;
  font-size: 10px;
  color: $grey-6-mayday;

  &.disabled {
    pointer-events: none;
  }

  .upvote {
    cursor: pointer;
    padding: 2px 4px 2px 6px;
    &:hover,
    &.selected {
      color: $green-5-mayday;
    }
  }

  .downvote {
    cursor: pointer;
    padding: 2px 6px 2px 4px;

    &:hover,
    &.selected {
      color: $red-5-mayday;
    }
  }
}

.feedback-form {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 16px;
}

.loading-container {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-top: 16px;
}

.loading-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: $grey-5-mayday;
  animation: loading 1s infinite ease-in-out;

  &:nth-child(2) {
    animation-delay: 0.2s;
  }

  &:nth-child(3) {
    animation-delay: 0.4s;
  }
}

.label-tooltip {
  max-width: 300px;
}

@keyframes loading {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.5);
  }
  100% {
    transform: scale(1);
  }
}
</style>
